import api from './api';

// Patient Profile API — Mongo-first, lightning-fast.
// All endpoints scoped by location_id.

export const patientApi = {
  list: (locationId, params = {}) =>
    api.get('/patients', { params: { location_id: locationId, ...params } }),

  // Single Mongo-only round-trip for the Patients List page mount: rows +
  // tag facets + custom-field facets in one call.
  listBundle: (locationId, params = {}) =>
    api.get('/patients/list-bundle', {
      params: { location_id: locationId, ...params },
    }),

  tags: (locationId, limit = 25) =>
    api.get('/patients/facets/tags', { params: { location_id: locationId, limit } }),

  ccfSync: (locationId) =>
    api.post('/patients/sync-custom-fields', null, { params: { location_id: locationId } }),

  ccfSyncStatus: (locationId) =>
    api.get('/patients/sync-custom-fields/status', { params: { location_id: locationId } }),

  syncAllContacts: (locationId) =>
    api.post('/patients/sync-all-contacts', null, { params: { location_id: locationId } }),

  syncAllContactsStatus: (locationId) =>
    api.get('/patients/sync-all-contacts/status', { params: { location_id: locationId } }),


  apptSync: (locationId, source = 'all') =>
    api.post('/patients/sync-appointments', null, { params: { location_id: locationId, source } }),

  apptSyncStatus: (locationId) =>
    api.get('/patients/sync-appointments/status', { params: { location_id: locationId } }),

  customFieldFacets: (locationId) =>
    api.get('/patients/facets/custom-fields', { params: { location_id: locationId } }),

  fieldDefinitions: (locationId) =>
    api.get('/patients/field-definitions', { params: { location_id: locationId } }),

  get: (contactId, locationId) =>
    api.get(`/patients/${contactId}`, { params: { location_id: locationId } }),

  // Single round-trip bundle for the profile page: patient + ai_insights +
  // activity + appointments. Backend fans out to Mongo in parallel (~65ms).
  profileBundle: (contactId, locationId) =>
    api.get(`/patients/${contactId}/profile-bundle`, {
      params: { location_id: locationId },
    }),

  lookup: (contactId, locationId) =>
    api.get(`/patients/${contactId}/lookup`, { params: { location_id: locationId } }),

  aiInsights: (contactId, locationId, refresh = false) =>
    api.get(`/patients/${contactId}/ai-insights`, {
      params: { location_id: locationId, refresh },
    }),

  appointments: (contactId, locationId) =>
    api.get(`/patients/${contactId}/appointments`, { params: { location_id: locationId } }),

  notes: (contactId, locationId) =>
    api.get(`/patients/${contactId}/notes`, { params: { location_id: locationId } }),

  createNote: (contactId, locationId, body, userId) =>
    api.post(`/patients/${contactId}/notes`, { body, user_id: userId }, {
      params: { location_id: locationId },
    }),

  updateNote: (contactId, noteId, locationId, body) =>
    api.put(`/patients/${contactId}/notes/${noteId}`, { body }, {
      params: { location_id: locationId },
    }),

  deleteNote: (contactId, noteId, locationId) =>
    api.delete(`/patients/${contactId}/notes/${noteId}`, {
      params: { location_id: locationId },
    }),

  tasks: (contactId, locationId) =>
    api.get(`/patients/${contactId}/tasks`, { params: { location_id: locationId } }),

  createTask: (contactId, locationId, data) =>
    api.post(`/patients/${contactId}/tasks`, data, {
      params: { location_id: locationId },
    }),

  updateTask: (contactId, taskId, locationId, data) =>
    api.put(`/patients/${contactId}/tasks/${taskId}`, data, {
      params: { location_id: locationId },
    }),

  communications: (contactId, locationId, limit = 50) =>
    api.get(`/patients/${contactId}/communications`, {
      params: { location_id: locationId, limit },
    }),

  callDetail: (contactId, callId, locationId) =>
    api.get(`/patients/${contactId}/calls/${callId}`, {
      params: { location_id: locationId },
    }),

  callRecordingUrl: (callId, locationId) =>
    `${api.defaults.baseURL}/calls/${callId}/recording?location_id=${encodeURIComponent(locationId)}`,

  pullCall: (callId, locationId, { reclassify = true } = {}) =>
    api.post(`/calls/${callId}/pull`, null, {
      params: { location_id: locationId, reclassify },
    }),

  customFields: (contactId, locationId) =>
    api.get(`/patients/${contactId}/custom-fields`, {
      params: { location_id: locationId },
    }),

  fieldAudit: (contactId, fieldId, locationId, limit = 5) =>
    api.get(`/patients/${contactId}/field-audit/${fieldId}`, {
      params: { location_id: locationId, limit },
    }),

  downloadIntakePdfUrl: (contactId, locationId) =>
    `${api.defaults.baseURL}/patients/${contactId}/intake-consent/download-pdf?location_id=${encodeURIComponent(locationId)}`,

  pushToJuvonno: (contactId, locationId) =>
    api.post(`/patients/${contactId}/intake-consent/push-to-juvonno`, {}, {
      params: { location_id: locationId },
    }),

  juvonnoPushHistory: (contactId, locationId) =>
    api.get(`/patients/${contactId}/intake-consent/juvonno-push-history`, {
      params: { location_id: locationId },
    }),

  intakeConsent: (contactId, locationId, refresh = false) =>
    api.get(`/patients/${contactId}/intake-consent`, {
      params: { location_id: locationId, refresh },
    }),

  sendIntake: (contactId, locationId, payload = {}) =>
    api.post(`/patients/${contactId}/intake-consent/send-intake`, payload, {
      params: { location_id: locationId },
    }),

  sendConsent: (contactId, locationId) =>
    api.post(`/patients/${contactId}/intake-consent/send-consent`, {}, {
      params: { location_id: locationId },
    }),

  resendConsent: (contactId, locationId, channels, customMessage = '') =>
    api.post(
      `/patients/${contactId}/intake-consent/resend-consent`,
      { channels, custom_message: customMessage },
      { params: { location_id: locationId } }
    ),

  markOfflineCompletion: (contactId, locationId, payload) =>
    api.post(`/patients/${contactId}/intake-consent/mark-offline`, payload, {
      params: { location_id: locationId },
    }),

  markInClinicCompletion: (contactId, locationId, formData) =>
    api.post(`/patients/${contactId}/intake-consent/mark-in-clinic`, formData, {
      params: { location_id: locationId },
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  removeOfflineCompletion: (contactId, recordId, locationId) =>
    api.delete(`/patients/${contactId}/intake-consent/mark-offline/${recordId}`, {
      params: { location_id: locationId },
    }),

  opportunity: (contactId, locationId) =>
    api.get(`/patients/${contactId}/opportunity`, {
      params: { location_id: locationId },
    }),

  activity: (contactId, locationId, opts = {}) => {
    // Backward compatible: legacy callers passed a number (limit).
    const { limit = 60, from_date, to_date } =
      typeof opts === 'number' ? { limit: opts } : opts;
    const params = { location_id: locationId, limit };
    if (from_date) params.from_date = from_date;
    if (to_date) params.to_date = to_date;
    return api.get(`/patients/${contactId}/activity`, { params });
  },

  resync: (contactId, locationId) =>
    api.post(`/patients/${contactId}/resync`, null, {
      params: { location_id: locationId },
    }),
};

export default patientApi;
